import { Box, Stack, Typography, Skeleton } from "@mui/material";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip } from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

const TREND_STATUS = {
	Strong: {
		value: 2,
		color: "#4ade80",
	},
	Declining: {
		value: 1,
		color: "#fb923c",
	},
	Inactive: {
		value: 0,
		color: "#94a3b8",
	},
};

const LEGEND_ITEMS = [
	{ label: "Strong", color: "#4ade80" },
	{ label: "Declining", color: "#fb923c" },
	{ label: "Inactive", color: "#94a3b8" },
];

const formatPeriod = (period) => {
	if (!period) return "";

	const [year, month] = period.split("-");
	const date = new Date(year, month - 1);

	return date.toLocaleString("default", {
		month: "short",
	});
};

const PerformanceTrend = ({ statuses, loading }) => {
	const hasData = Array.isArray(statuses) && statuses.length > 0;

	if (loading || !hasData) {
		return (
			<Box
				sx={{
					flex: 1,
					p: "16px 18px",
					backgroundColor: "background.paper",
					border: "1px solid rgba(29,84,162,0.1)",
					borderRadius: "12px",
					boxShadow: "0 1px 8px rgba(29,84,162,0.06)",
				}}
			>
				<Stack direction="row" justifyContent="space-between" mb={4}>
					<Stack direction="row" alignItems="center" gap={0.75}>
						<Skeleton variant="circular" width={8} height={8} />
						<Skeleton variant="text" width={140} height={20} />
					</Stack>

					<Stack direction="row" gap={2}>
						{LEGEND_ITEMS.map(({ label }) => (
							<Stack key={label} direction="row" alignItems="center" gap={0.75}>
								<Skeleton variant="circular" width={8} height={8} />
								<Skeleton variant="text" width={50} height={14} />
							</Stack>
						))}
					</Stack>
				</Stack>

				<Skeleton variant="rounded" width="100%" height={120} />

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(3, 1fr)",
						gap: 1,
						mt: 1.75,
					}}
				>
					{[0, 1, 2].map((i) => (
						<Box
							key={i}
							sx={{
								backgroundColor: "background.default",
								borderRadius: 1.5,
								p: "8px 10px",
							}}
						>
							<Skeleton variant="text" width="70%" height={12} />
							<Skeleton variant="text" width="30%" height={22} />
						</Box>
					))}
				</Box>
			</Box>
		);
	}

	const currentTrend = statuses[statuses.length - 1]?.trend ?? "Inactive";

	const currentColor = TREND_STATUS[currentTrend]?.color ?? TREND_STATUS.Inactive.color;

	const strong = statuses.filter((item) => item.trend === "Strong").length;
	const declining = statuses.filter((item) => item.trend === "Declining").length;
	const inactive = statuses.filter((item) => item.trend === "Inactive").length;
	const labels = statuses.map((item) => formatPeriod(item.period));

	const values = statuses.map((item) => TREND_STATUS[item.trend]?.value ?? 0);

	const pointColors = statuses.map((item) => TREND_STATUS[item.trend]?.color ?? TREND_STATUS.Inactive.color);

	// Duplicate single point so Chart.js renders a flat line
	const chartLabels = labels.length === 1 ? [labels[0], labels[0]] : labels;
	const chartValues = values.length === 1 ? [values[0], values[0]] : values;
	const chartPointColors = pointColors.length === 1 ? [pointColors[0], pointColors[0]] : pointColors;

	const chartData = {
		labels: chartLabels,
		datasets: [
			{
				data: chartValues,
				borderColor: "#3A7DC9",
				borderWidth: 2,
				tension: 0.35,
				fill: false,
				pointRadius: 6,
				pointHoverRadius: 8,
				pointBackgroundColor: chartPointColors,
				pointBorderColor: chartPointColors,
				pointBorderWidth: 2,
			},
		],
	};

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				backgroundColor: "#fff",
				titleColor: "#444",
				bodyColor: "#444",
				borderColor: "#e0e0e0",
				borderWidth: 0.5,
				padding: 8,
				callbacks: {
					label: (ctx) => ` ${statuses[ctx.dataIndex]?.trend ?? statuses[0]?.trend ?? ""}`,
				},
			},
		},
		scales: {
			x: {
				grid: {
					display: false,
				},
				border: {
					display: false,
				},
				ticks: {
					font: {
						size: 10,
					},
					color: "#888",
					maxRotation: 0,
				},
			},
			y: {
				display: false,
				min: -0.5,
				max: 2.5,
			},
		},
	};

	return (
		<Box
			sx={{
				flex: 1,
				p: "16px 18px",
				backgroundColor: "background.paper",
				border: "1px solid rgba(29,84,162,0.1)",
				borderRadius: "12px",
				boxShadow: "0 1px 8px rgba(29,84,162,0.06)",
			}}
		>
			<Stack direction="row" justifyContent="space-between" mb={4}>
				<Stack direction="row" alignItems="center" gap={0.75}>
					<Box
						sx={{
							width: 8,
							height: 8,
							borderRadius: "50%",
							backgroundColor: currentColor,
						}}
					/>

					<Typography variant="subtitle2" fontWeight={500}>
						<strong style={{ color: currentColor }}>{currentTrend}</strong>

						<Typography component="span" variant="subtitle2" color="text.secondary" fontWeight={400}>
							{" "}
							— current month
						</Typography>
					</Typography>
				</Stack>

				<Stack direction="row" gap={2}>
					{LEGEND_ITEMS.map(({ label, color }) => (
						<Stack key={label} direction="row" alignItems="center" gap={0.75}>
							<Box
								sx={{
									width: 8,
									height: 8,
									borderRadius: "50%",
									backgroundColor: color,
								}}
							/>

							<Typography
								sx={{
									fontSize: "0.7rem",
									color: "text.secondary",
								}}
							>
								{label}
							</Typography>
						</Stack>
					))}
				</Stack>
			</Stack>

			<Box sx={{ height: 120 }}>
				<Line data={chartData} options={chartOptions} />
			</Box>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(3, 1fr)",
					gap: 1,
					mt: 1.75,
				}}
			>
				{[
					{
						label: "Strong months",
						value: strong,
						color: "#4ade80",
					},
					{
						label: "Declining months",
						value: declining,
						color: "#fb923c",
					},
					{
						label: "Inactive months",
						value: inactive,
						color: "#94a3b8",
					},
				].map(({ label, value, color }) => (
					<Box
						key={label}
						sx={{
							backgroundColor: "background.default",
							borderRadius: 1.5,
							p: "8px 10px",
						}}
					>
						<Typography
							sx={{
								fontSize: "0.6rem",
								textTransform: "uppercase",
								letterSpacing: "0.05em",
								color: "text.secondary",
								mb: 0.25,
							}}
						>
							{label}
						</Typography>

						<Typography
							sx={{
								fontSize: "1.25rem",
								fontWeight: 600,
								color,
							}}
						>
							{value}
						</Typography>
					</Box>
				))}
			</Box>
		</Box>
	);
};

export default PerformanceTrend;
